// Sprint chat-header-restore-name-drop-idle (2026-05-24) — partial
// reversal of chat-header-slim. The chat-header-slim sprint mis-read
// Santi's intent: he wanted the AGENT NAME kept and the StatusPill
// `Idle · Send a message…` dropped. The implementer did the inverse.
//
// Layout (left → right) — restored:
//   [☰ hamburger (mobile)]  [name (double-click rename)]
//   [status pill — only when running] [branch chip] [↑↓ ahead/behind]
//   [R1 runner chip] [DualMode chip] … [⋯ kebab DropdownMenu]
//
// Still removed (correct part of chat-header-slim):
// - server/host chip ("VPS-CONTROL fsn1") — R1 already identifies the runner.
// - project chip — sidebar groups by project; composer's ProjectChip is the
//   single in-chat surface for project confirmation.

import { useState } from 'react';
import type { Agent, EffortLevel } from '../types';
import { useAppStore } from '../stores/app.store';
import { patchAgentConfig } from '../lib/api';
import { reconcileEffort } from '../lib/models';
import { ConfirmDialog } from './ConfirmDialog';
import { BranchChip } from './BranchChip';
import { AheadBehindChip } from './AheadBehindChip';
import { RunnerChip } from './RunnerChip';
import { DualModeHeaderChip } from './founder/DualModeChip';
import { Icon } from './atoms/Icon';
import { StatusPill } from './atoms/StatusPill';
import { useInlineRename } from '../hooks/useInlineRename';
import { useMediaQuery, MD_QUERY } from '../hooks/useMediaQuery';
import { useTranslation } from '../i18n';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export interface ChatHeaderProps {
  agent: Agent;
  onStop: () => Promise<void>;
  onFork: () => Promise<void>;
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
  onOpenSidebar?: () => void;
}

type Confirming = 'stop' | 'fork' | 'delete' | null;

export function ChatHeader(props: ChatHeaderProps) {
  const { agent, onStop, onFork, onRename, onDelete, onOpenSidebar } = props;
  const { t } = useTranslation();
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [configError, setConfigError] = useState<string | null>(null);
  const isDesktop = useMediaQuery(MD_QUERY);

  const rename = useInlineRename({
    initial: agent.name,
    onCommit: async (next) => {
      await onRename(next);
    },
  });

  // Sprint 5.6 (B7) — chip config handler retained for future surfaces.
  async function applyConfig(next: { model?: string; effort?: EffortLevel }) {
    const optimistic: Agent = {
      ...agent,
      model: next.model ?? agent.model,
      effort: next.effort ?? agent.effort,
    };
    upsertAgent(optimistic);
    setConfigError(null);
    try {
      const updated = await patchAgentConfig(agent.id, next);
      upsertAgent(updated);
    } catch (err) {
      upsertAgent(agent);
      setConfigError(
        err instanceof Error ? err.message : 'Could not change model',
      );
    }
  }
  void applyConfig;
  void reconcileEffort;

  const [isStopping, setIsStopping] = useState(false);
  const [isForking, setIsForking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  async function runStop() {
    setIsStopping(true);
    try {
      await onStop();
      setConfirming(null);
    } finally {
      setIsStopping(false);
    }
  }

  async function runFork() {
    setIsForking(true);
    try {
      await onFork();
      setConfirming(null);
    } finally {
      setIsForking(false);
    }
  }

  async function runDelete() {
    setIsDeleting(true);
    try {
      await onDelete();
      setConfirming(null);
    } finally {
      setIsDeleting(false);
    }
  }

  function startRename() {
    rename.begin();
  }

  const nameClickHandlers = isDesktop
    ? { onDoubleClick: () => rename.begin() }
    : { onClick: () => rename.begin() };

  return (
    <TooltipProvider delayDuration={300}>
      <header
        className="flex items-center gap-2 px-2 md:px-4 h-14 border-b border-border bg-background shrink-0 pt-safe-top box-content"
        data-testid="chat-header"
      >
        {onOpenSidebar && <HamburgerButton onClick={onOpenSidebar} />}

        <div className="flex-1 min-w-0 flex items-center gap-2">
          {rename.editing ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                void rename.commit();
              }}
              className="flex-1 min-w-0"
            >
              <Input
                autoFocus
                value={rename.value}
                onChange={(e) => rename.setValue(e.target.value)}
                onBlur={() => void rename.commit()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') rename.cancel();
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void rename.commit();
                  }
                }}
                disabled={rename.busy}
                className="h-8 bg-card border-border text-sm"
                data-testid="rename-input"
              />
            </form>
          ) : (
            <>
              <h1
                className="text-sm font-semibold text-foreground truncate cursor-text select-none"
                data-testid="chat-header-name"
                title={isDesktop ? t('chatHeader.renameTooltipDesktop') : t('chatHeader.renameTooltipMobile')}
                {...nameClickHandlers}
              >
                {agent.name}
              </h1>

              {/* Sprint chat-header-restore-name-drop-idle — only render
                  the pill when the agent is actively running. In idle /
                  waiting / terminal states the composer is already the
                  affordance to send the next message; the pill copy was
                  noise. */}
              {agent.status === 'running' && (
                <StatusPill status={agent.status} />
              )}

              <div className="hidden sm:flex shrink-0" data-testid="chat-header-branch-slot">
                <BranchChip gitStatus={agent.gitStatus ?? null} />
              </div>

              <div className="hidden sm:flex shrink-0" data-testid="chat-header-aheadbehind-slot">
                <AheadBehindChip gitStatus={agent.gitStatus ?? null} />
              </div>

              {/* Sprint 20.3 PR-9 (D20 / UC-38) — runner indicator chip.
                  Sits between the git-status chips and the dual-mode chip
                  so the founder reads "what code · which runner" left-to-right. */}
              <div className="hidden sm:flex shrink-0" data-testid="chat-header-runner-slot">
                <RunnerChip runnerId={agent.runnerId} />
              </div>

              <DualModeHeaderChip />
            </>
          )}
        </div>

        {configError && (
          <p
            className="text-xs text-destructive mr-2"
            role="alert"
            data-testid="chat-header-config-error"
          >
            {configError}
          </p>
        )}

        <div className="flex items-center gap-1 shrink-0">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('chatHeader.moreActions')}
                    data-testid="menu-button"
                    className="h-8 w-8 text-muted-foreground"
                    style={{ minHeight: 'auto', minWidth: 'auto' }}
                  >
                    <Icon name="MoreHorizontal" size={18} aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {t('chatHeader.moreActions')}
              </TooltipContent>
            </Tooltip>

            <DropdownMenuContent align="end" className="min-w-[10rem]" data-testid="header-menu">
              <DropdownMenuItem
                onSelect={startRename}
                data-testid="menu-rename"
              >
                <Icon name="Pencil" size={14} className="mr-2" aria-hidden="true" />
                {t('contextMenu.rename')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => setConfirming('delete')}
                className="text-destructive focus:text-destructive"
                data-testid="menu-delete"
              >
                <Icon name="Trash2" size={14} className="mr-2" aria-hidden="true" />
                {t('contextMenu.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <ConfirmDialog
          open={confirming === 'stop'}
          title={t('confirm.stopAgent.title')}
          body={t('confirm.stopAgent.body')}
          confirmLabel={t('confirm.stopAgent.confirm')}
          destructive
          loading={isStopping}
          onConfirm={runStop}
          onCancel={() => setConfirming(null)}
        />
        <ConfirmDialog
          open={confirming === 'fork'}
          title={t('confirm.forkAgent.title')}
          body={t('confirm.forkAgent.body')}
          confirmLabel={t('confirm.forkAgent.confirm')}
          loading={isForking}
          onConfirm={runFork}
          onCancel={() => setConfirming(null)}
        />
        <ConfirmDialog
          open={confirming === 'delete'}
          title={t('confirm.deleteAgent.title')}
          body={t('confirm.deleteAgent.body')}
          confirmLabel={t('confirm.deleteAgent.confirm')}
          destructive
          loading={isDeleting}
          onConfirm={runDelete}
          onCancel={() => setConfirming(null)}
        />
      </header>
    </TooltipProvider>
  );
}

function HamburgerButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={() => {
        if (
          typeof document !== 'undefined' &&
          document.activeElement instanceof HTMLElement
        ) {
          document.activeElement.blur();
        }
        onClick();
      }}
      aria-label={t('chatHeader.backAriaLabel')}
      data-testid="hamburger-button"
      className="md:hidden h-9 w-9 text-muted-foreground"
      style={{ minHeight: 'auto', minWidth: 'auto' }}
    >
      <Icon name="Menu" size={18} aria-hidden="true" />
    </Button>
  );
}
